<template>
  <div
    v-on-visible="{ fct: doVisible, once: true }"
    class="box-border relative overflow-hidden transition-shadow duration-200 ease-in-out bg-white border border-solid rounded-md cursor-pointer group shadow-card hover:shadow-xl" :class="[{ 'border-primary-500': selected }, `t-${size}`]"
    @click="doClick"
  >
    <!-- No impact on UI <div class="absolute top-0 left-0 w-full h-full bg-white opacity-75" v-if="!article._IsAvailable" /> -->
    <div class="absolute top-0 left-0 z-20 easy-check" @click.stop="doSelectionChanged" />
    <div
      v-if="allowSelection"
      class="box-border z-10 w-3 h-3 bg-white border rounded-sm border-dark-50 group-hover:block check"
      :class="[selectionMode || selected ? 'block' : 'hidden']"
    >
      <font-awesome-icon v-show="selected" class="w-4 h-4 -ml-0.5 -mt-1 text-primary-500" icon="fa-light fa-check" />
    </div>
    <div class="image" :data-img="getImage">
      <div v-if="!visible || getImage === ''" class="animate-pulse w-full bg-gray-300 h-48 rounded-lg mb-5 flex justify-center items-center">
        <svg class="w-8 h-8 stroke-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.5499 15.15L19.8781 14.7863C17.4132 13.4517 16.1808 12.7844 14.9244 13.0211C13.6681 13.2578 12.763 14.3279 10.9528 16.4679L7.49988 20.55M3.89988 17.85L5.53708 16.2384C6.57495 15.2167 7.09388 14.7059 7.73433 14.5134C7.98012 14.4396 8.2352 14.4011 8.49185 14.3993C9.16057 14.3944 9.80701 14.7296 11.0999 15.4M11.9999 21C12.3154 21 12.6509 21 12.9999 21C16.7711 21 18.6567 21 19.8283 19.8284C20.9999 18.6569 20.9999 16.7728 20.9999 13.0046C20.9999 12.6828 20.9999 12.3482 20.9999 12C20.9999 11.6845 20.9999 11.3491 20.9999 11.0002C20.9999 7.22883 20.9999 5.34316 19.8283 4.17158C18.6568 3 16.7711 3 12.9998 3H10.9999C7.22865 3 5.34303 3 4.17145 4.17157C2.99988 5.34315 2.99988 7.22877 2.99988 11C2.99988 11.349 2.99988 11.6845 2.99988 12C2.99988 12.3155 2.99988 12.651 2.99988 13C2.99988 16.7712 2.99988 18.6569 4.17145 19.8284C5.34303 21 7.22921 21 11.0016 21C11.3654 21 11.7021 21 11.9999 21ZM7.01353 8.85C7.01353 9.84411 7.81942 10.65 8.81354 10.65C9.80765 10.65 10.6135 9.84411 10.6135 8.85C10.6135 7.85589 9.80765 7.05 8.81354 7.05C7.81942 7.05 7.01353 7.85589 7.01353 8.85Z" stroke="stroke-current" stroke-width="1.6" stroke-linecap="round" />
        </svg>
      </div>
      <no-image v-else-if="errorLoadingImg" class="text-red-500" />

      <img
        v-else :src="getImage" :style="{ width: `${getImageSize}px`, height: `${getImageSize}px` }"
        @error="errorLoadingImg = true"
      >
    </div>
    <div class="absolute flex flex-row flex-wrap details">
      <div>
        <div v-if="asset.document.FileName" v-text="getThumbnailAttributeValue" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import appConfig from '@/services/appConfig'
import type { SearchAssetModel } from '@/api/dune/model/searchAssetModel'
import NoImage from '@/shared/components/svg/NoImage.vue'
import { useUserStore } from '@/store/userData'

const props = withDefaults(defineProps<IProps>(), { size: 3, selectionMode: false, allowSelection: true })

const emit = defineEmits<{
  (e: 'click', ev: MouseEvent): void
  (e: 'update:selected', value: boolean): void
  (e: 'selectionChanged', value: boolean): void
}>()

interface IProps {
  context: string
  contextKey: string
  asset: SearchAssetModel
  selected: boolean
  size?: number
  selectionMode?: boolean
  allowSelection?: boolean
}
const userStore = useUserStore()

const visible = ref(false)
const errorLoadingImg = ref(false)

const getImage = computed(() => {
  const params = new URLSearchParams()
  params.set('Context', props.context)
  params.set('ContextKey', props.contextKey)
  params.set('w', '180')
  params.set('h', '180')
  params.set('trim', 'false')
  params.set('f', 'webp')

  return `${appConfig.AssetsUrl}/assets/content/${props.asset.document.StorageFile.toUpperCase()}?${params.toString()}`
})

const getImageSize = computed(() => {
  switch (props.size) {
    case 3:
      return 180
    case 2:
      return 105
    default:
      return 60
  }
})

const getThumbnailAttributeValue = computed(() => {
  let attributeValue: string = props.asset.document.Metadata?.ColorwayName || props.asset.document.FileName

  // Check for catalog or seller configurations
  if (userStore.activeCatalog?.Config?.DigitalAssetThumbnailAttribute?.length) {
    attributeValue = props.asset.document.Metadata?.[userStore.activeCatalog?.Config?.DigitalAssetThumbnailAttribute] || props.asset.document[userStore.activeCatalog?.Config?.DigitalAssetThumbnailAttribute]
  }
  else if (userStore.sellerConfig?.DigitalAssetThumbnailAttribute?.length) {
    attributeValue = props.asset.document.Metadata?.[userStore.sellerConfig?.DigitalAssetThumbnailAttribute] || props.asset.document[userStore.sellerConfig?.DigitalAssetThumbnailAttribute]
  }

  return attributeValue
})

function doVisible() {
  visible.value = true
}

function doClick(ev: MouseEvent) {
  if (props.selectionMode) {
    doSelectionChanged()
  }
  else {
    emit('click', ev)
  }
}

function doSelectionChanged() {
  const newVal = !props.selected
  emit('update:selected', newVal)
  emit('selectionChanged', newVal)
}
</script>

<style lang="scss" scoped>
.t-1 {
  width: 95px;
  height: 126px;

  .easy-check {
    width: 25px;
    height: 25px;
  }

  .check {
    position: absolute;
    top: 5px;
    left: 5px;
  }

  .image {
    position: absolute;
    width: 60px;
    height: 60px;
    top: 10px;
    left: 18px;
  }

  .fav {
    position: absolute;
    overflow: hidden;
    white-space: nowrap;
    top: 5px;
    right: 5px;
    width: 72px;
    height: 10px;
    z-index: 1;
  }

  .details {
    position: absolute;
    top: 80px;
    left: 0px;
    width: 95px;
    height: 46px;
    padding: 0 5px 5px 5px;
  }

  .watermark {
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    transform: rotate(-45deg);
    text-align: center;
    font-weight: 1000;
    font-size: 10px;
    z-index: 1;
  }
}

.t-2 {
  width: 125px;
  height: 178px;

  .easy-check {
    width: 35px;
    height: 35px;
  }

  .check {
    position: absolute;
    top: 12px;
    left: 10px;
  }

  .image {
    position: absolute;
    width: 105px;
    height: 105px;
    top: 10px;
    left: 10px;
  }

  .fav {
    position: absolute;
    overflow: hidden;
    white-space: nowrap;
    top: 10px;
    right: 10px;
    width: 80px;
    height: 10px;
    z-index: 1;
  }

  .details {
    position: absolute;
    top: 126px;
    left: 0px;
    width: 125px;
    height: 50px;
    padding: 0 5px 5px 5px;
  }

  .watermark {
    position: absolute;
    top: 30px;
    left: 0;
    right: 0;
    transform: rotate(-45deg);
    text-align: center;
    font-weight: 1000;
    font-size: 15px;
    z-index: 1;
  }
}

.t-3 {
  width: 200px;
  height: 250px;

  .easy-check {
    width: 35px;
    height: 35px;
  }

  .check {
    position: absolute;
    top: 12px;
    left: 10px;
  }

  .image {
    position: absolute;
    top: 10px;
    left: 10px;
    width: 180px;
    height: 180px;
  }

  .fav {
    position: absolute;
    overflow: hidden;
    white-space: nowrap;
    top: 10px;
    right: 10px;
    width: 95px;
    height: 10px;
    z-index: 1;
  }

  .details {
    position: absolute;
    top: 200px;
    left: 0px;
    width: 200px;
    height: 40px;
    padding: 0 10px 0px 10px;
  }

  .watermark {
    position: absolute;
    top: 40px;
    left: 0;
    right: 0;
    transform: rotate(-45deg);
    text-align: center;
    font-weight: 1000;
    font-size: 18px;
    z-index: 1;
  }
}
</style>
